// Nativní HTTP pro odesílání na pozadí.
//
// PROČ: Android po ~5 minutách na pozadí **přiškrtí HTTP požadavky vycházející
// z WebView** (tedy `fetch`). Foreground service přitom dál běží a polohu sbírá —
// jen se nemá jak odeslat. Přesně tohle jsme viděli 3. 8. 2026: notifikace v liště,
// zelená tečka, baterie neomezená, oprávnění „Povolit vždy" — a přesto na serveru
// nic. README `@capacitor-community/background-geolocation` to popisuje výslovně:
//
//   "If your app forwards location updates to a server in real time, be aware that
//    after 5 minutes in the background Android will throttle HTTP requests initiated
//    from the WebView. The solution is to use a native HTTP plugin."
//
// `CapacitorHttp` je v `@capacitor/core`, žádná další závislost.
//
// ZÁMĚRNĚ NEZAPÍNÁME globální patch `CapacitorHttp: { enabled: true }` — ten
// přesměruje veškerý `fetch` v appce a rozbíjí nahrávání fotek (FormData/Blob).
// Nativní cestou posíláme jen to, co musí projít i na pozadí: dávky GPS a eventů.

import { Capacitor, CapacitorHttp } from "@capacitor/core";

export interface NativePostResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  error?: string;
}

/** True = jsme v nativní appce, kde má smysl obcházet WebView. */
export const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * POST přes nativní vrstvu. Na webu vrací `ok: false` a volající spadne na `fetch` —
 * v prohlížeči totiž problém s přiškrcením neexistuje a `CapacitorHttp` tam není.
 */
export async function nativePost<T>(
  url: string,
  body: unknown,
  headers: Record<string, string>,
): Promise<NativePostResult<T>> {
  if (!isNative()) {
    return { ok: false, status: 0, data: null, error: "not-native" };
  }
  try {
    const res = await CapacitorHttp.post({
      url,
      headers: { "Content-Type": "application/json", ...headers },
      data: body,
      // Nechceme, aby se plugin snažil odpověď „chytře" parsovat jinak než jako JSON.
      responseType: "json",
    });
    const ok = res.status >= 200 && res.status < 300;
    return {
      ok,
      status: res.status,
      data: ok ? (res.data as T) : null,
      error: ok ? undefined : `HTTP ${res.status}`,
    };
  } catch (e) {
    return { ok: false, status: 0, data: null, error: (e as Error)?.message ?? "native post failed" };
  }
}
